import { login as unhandledLogin } from '~/AccessToken';
import { PasswordLoginErrorResponse } from '~/Api/Authentication/Password';
import { redirectToBacklink } from '~/Backlink';
import config from '~/config';
import { handleError } from '~/ErrorHandler';
import isValidEmail from '~/utils/Email/isValidEmail';
import isValidPassword from '~/utils/Password/isValidPassword';
import { ThunkAction } from '~/Store';

import { LoginResult } from './LoginPage';
import { replace } from 'connected-react-router';

const InvalidEmailErrorMessage = 'Please check that you entered your email correctly';

export function login(email: string, password: string): ThunkAction<Promise<LoginResult>, any> {
	return async (dispatch, getState) => {
		const result: LoginResult = { emailError: null, passwordError: null };

		if (!isValidEmail(email)) {
			result.emailError = InvalidEmailErrorMessage;
		}
		if (!isValidPassword(password)) {
			result.passwordError = 'Password should be at least 8 characters long';
		}

		if (result.emailError !== null || result.passwordError !== null) {
			return Promise.resolve(result);
		}

		try {
			const passwordLoginResult = await dispatch(unhandledLogin(email, password));
			if (!passwordLoginResult.gdprTermsConsent && passwordLoginResult.accessToken.impersonatedBy === null) {
				const {
					backlink: { backlinks },
				} = getState();
				// we need to get previous location in order to redirect user there after GDPR consent.
				const previousLocation = backlinks[backlinks.length - 1];
				dispatch(replace({ pathname: '/gdpr-consent', state: { originalLocation: previousLocation } }));
			} else {
				dispatch(redirectToBacklink({ pathname: '/product-groups/', hash: '', search: '' }));
			}
		} catch (error) {
			if (!(error instanceof PasswordLoginErrorResponse)) {
				dispatch(handleError(error));
				return result;
			}

			if (error.handleUserNotFound()) {
				result.emailError = 'Unknown email, please check your email and try it again';
			}
			if (error.handlePasswordDoesNotMatch()) {
				result.passwordError = 'Incorrect password';
			}
			if (error.handleInvalidEmail()) {
				result.emailError = InvalidEmailErrorMessage;
			}
			if (error.handleAuthenticationAccountRegistrationNotCompleted()) {
				result.passwordError = 'Your account was not activated yet, please follow instructions sent to you by email';
			}
			if (error.handleAuthenticationExpiredAccount()) {
				result.passwordError = `Your user account has expired, please contact ${config.support.email}`;
			}
			if (error.handleAuthenticationAccountDeactivated()) {
				result.passwordError = `Your user account has been deactivated, please contact ${config.support.email}`;
			}

			dispatch(handleError(error));
		}

		return result;
	};
}
